import { gsap } from 'gsap';
import type { ThreeStage } from '../../../../three/ThreeStage';
import { BaseScene } from '../../../../core/BaseScene';
import { draw, pulse } from '../../../../components/svg';
import { chars, words, revealWords, settleWords } from '../../../../components/text';

interface Layer {
  label: string;
  nodes: number[];
  /** Index into nodes that the question passes through. */
  via: number;
}

const LAYERS: Layer[] = [
  { label: 'CONTEXT', nodes: [640, 800, 960], via: 1 },
  { label: 'KNOWLEDGE', nodes: [520, 660, 800, 940, 1080], via: 3 },
  { label: 'RETRIEVAL', nodes: [580, 740, 900, 1060], via: 1 },
  { label: 'SPECIALISED ANALYSIS', nodes: [500, 700, 900, 1100], via: 2 },
  { label: 'EVIDENCE', nodes: [600, 800, 1000], via: 1 },
  { label: 'REASONING', nodes: [700, 900], via: 0 },
  { label: 'RESPONSE', nodes: [800], via: 0 },
];
const TOP = 196;
const GAP = 76;
const layerY = (i: number): number => TOP + i * GAP;
const AGENTS = 3;
const EVIDENCE_LAYER = 4;
const EVIDENCE = ['ops review · p.12', 'customer survey', 'usage data'];

/** Which layers are lit at each step. */
const LIT_UNTIL = [-1, 2, 4, 6, 6, 6];

/**
 * 0 The question
 * 1 Context → knowledge → retrieval
 * 2 Specialised analysis collaborates; evidence is gathered
 * 3 Reasoning → response, with its evidence attached
 * 4 NOT JUST ANSWERS.
 * 5 CONTEXT-AWARE INTELLIGENCE.
 */
export class MintIntelligenceScene extends BaseScene {
  public readonly stepCount = 6;

  private collaborating = false;

  protected template(): string {
    const trace: string[] = [];
    let from = { x: 800, y: 118 };
    LAYERS.forEach((layer, i) => {
      const to = { x: layer.nodes[layer.via], y: layerY(i) };
      const midY = (from.y + to.y) / 2;
      trace.push(`<path class="mi-trace draw" data-layer="${i}" pathLength="1" d="M${from.x} ${from.y} C${from.x} ${midY} ${to.x} ${midY} ${to.x} ${to.y}"/>`);
      from = to;
    });

    const layers = LAYERS.map((layer, i) => {
      const y = layerY(i);
      const nodes = layer.nodes.map((x, n) => {
        const isAgent = i === AGENTS;
        const isEvidence = i === EVIDENCE_LAYER;
        return `<g class="mi-node${isAgent ? ' mi-node--agent' : ''}${n === layer.via ? ' mi-node--via' : ''}" transform="translate(${x} ${y})">
          ${isAgent ? '<circle class="mi-node__halo" r="22"/>' : ''}
          <circle class="mi-node__dot" r="${isAgent ? 9 : 5}"/>
          ${isEvidence ? `<text class="mi-evidence" y="30" text-anchor="middle">${EVIDENCE[n]}</text>` : ''}
        </g>`;
      }).join('');
      return `<g class="mi-layer" data-layer="${i}">
        <path class="mi-layer__line" d="M380 ${y} H1220"/>
        <text class="mi-layer__label" x="350" y="${y + 5}" text-anchor="end">${layer.label}</text>
        ${nodes}
      </g>`;
    }).join('');

    // Every specialist talks to every other specialist.
    const agentX = LAYERS[AGENTS].nodes;
    const agentY = layerY(AGENTS);
    const links: string[] = [];
    for (let a = 0; a < agentX.length; a += 1) {
      for (let b = a + 1; b < agentX.length; b += 1) {
        const lift = 26 + (b - a) * 18;
        links.push(`<path class="mi-collab draw" pathLength="1" d="M${agentX[a]} ${agentY} Q${(agentX[a] + agentX[b]) / 2} ${agentY - lift} ${agentX[b]} ${agentY}"/>`);
      }
    }
    const collabPulses = links.map(() => '<circle class="mi-pulse" r="3.5"/>').join('');

    return `
      <div class="scene-layout mint-intelligence">
        <p class="mi-question" aria-label="Where should we focus next?">${chars('“Where should we focus next?”')}</p>
        <svg class="diagram mi-diagram" viewBox="0 0 1600 900" role="img" aria-label="A question passing through context, knowledge, retrieval, specialised analysis, evidence, reasoning and response">
          ${layers}
          <g class="mi-collabs">${links.join('')}</g>
          <g class="mi-traces">${trace.join('')}</g>
          <g class="mi-pulses">${collabPulses}</g>
          <circle class="mi-token" r="8"/>
        </svg>
        <div class="mi-answer" data-from="3">
          <b>Focus on customer onboarding.</b>
          <span class="mi-answer__evidence">${EVIDENCE.map((e) => `<i>${e}</i>`).join('')}</span>
        </div>
        <div class="statement-stack mi-statements">
          <h2 class="statement" data-from="4">${words('NOT JUST ANSWERS.')}</h2>
          <h2 class="statement statement--accent" data-from="5">${words('CONTEXT-AWARE INTELLIGENCE.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.mi-question .char'), { opacity: 0 }, { opacity: 1, duration: 0.05, stagger: 0.045, ease: 'none' }, 0.3);
    tl.fromTo(this.$$('.mi-layer'), { opacity: 0 }, { opacity: 1, duration: 0.8, stagger: 0.07, ease: 'power2.out' }, 0.8);
  }

  protected onExit(): void {
    this.collaborating = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('mint-intelligence', step, immediate);

    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    const lit = LIT_UNTIL[step];
    const previousLit = forward ? LIT_UNTIL[previousStep] ?? -1 : lit;

    gsap.set(this.$$('.mi-question .char'), { opacity: 1 });
    gsap.set(this.$$('.mi-layer'), { opacity: 1 });

    // Settle layers and trace to this step's state, then animate the newly reached layers.
    this.$$('.mi-layer').forEach((layer, i) => layer.classList.toggle('is-lit', i <= previousLit));
    this.$$<SVGPathElement>('.mi-trace').forEach((path, i) => draw(path, i <= previousLit, { immediate: true }));
    this.placeToken(previousLit, true);

    for (let i = previousLit + 1; i <= lit; i += 1) {
      const at = (i - previousLit - 1) * 0.55;
      tl.add(draw(this.$(`.mi-trace[data-layer="${i}"]`), true, { duration: 0.5, ease: 'power1.inOut' }), at);
      tl.call(() => {
        this.$(`.mi-layer[data-layer="${i}"]`).classList.add('is-lit');
        this.placeToken(i, false);
      }, [], at + 0.45);
    }

    // Collaboration between specialists runs once analysis is reached.
    const collabs = this.$$<SVGPathElement>('.mi-collab');
    const collaborate = lit >= AGENTS;
    if (collaborate && forward && previousLit < AGENTS) {
      draw(collabs, false, { immediate: true });
      tl.add(draw(collabs, true, { duration: 0.8, stagger: 0.1 }), (AGENTS - previousLit - 1) * 0.55 + 0.4);
    } else {
      draw(collabs, collaborate, { immediate: true });
    }
    this.syncCollaboration(collaborate);

    // The answer appears with its evidence; then the diagram steps back for the statements.
    const evidence = this.$$('.mi-answer__evidence i');
    if (forward && step === 3) tl.fromTo(evidence, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.15 }, 1.6);
    else gsap.set(evidence, { opacity: 1, y: 0 });

    const statements = step >= 4;
    gsap.to([this.$('.mi-diagram'), this.$('.mi-answer'), this.$('.mi-question')], { opacity: statements ? 0.14 : 1, duration: immediate ? 0 : 0.8, ease: 'power2.inOut' });
    this.$$('.mi-statements .statement').forEach((statement) => settleWords(statement));
    if (forward && step >= 4) tl.add(revealWords(this.$$('.mi-statements .statement')[step - 4]), 0.2);
  }

  private placeToken(layer: number, immediate: boolean): void {
    const token = this.$<SVGCircleElement>('.mi-token');
    const point = layer < 0 ? { x: 800, y: 118 } : { x: LAYERS[layer].nodes[LAYERS[layer].via], y: layerY(layer) };
    gsap.to(token, { attr: { cx: point.x, cy: point.y }, duration: immediate ? 0 : 0.2, overwrite: 'auto' });
    token.style.opacity = layer < 0 ? '0' : '1';
  }

  private syncCollaboration(on: boolean): void {
    if (on === this.collaborating) return;
    this.collaborating = on;
    if (!on) {
      this.stopLoops();
      this.$$('.mi-pulse').forEach((marker) => { marker.style.opacity = '0'; });
      return;
    }
    const collabs = this.$$<SVGPathElement>('.mi-collab');
    this.$$<SVGCircleElement>('.mi-pulse').forEach((marker, i) => {
      this.loop(pulse(marker, collabs[i], { duration: 0.9 + (i % 3) * 0.25, delay: 1 + i * 0.35, repeatDelay: 0.6 + (i % 2) * 0.8 }));
    });
  }
}
